import styled from '@emotion/styled';
import { Button, Flex, type MenuProps, Modal, Space, Typography } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { isNewBuildAvailable } from 'src/lib/buildState';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { useIsChildAccount } from 'src/lib/customHooks/useIsChildAccount';
import { trackEvent } from 'src/lib/googleAnalytics';
import { staticThemeToken } from 'src/lib/theme';
import { UpdateStream } from '../AnnounceKit';
import { IconExclamationTriangle } from '../BasicComponents/Icons';
import { VersionLabel } from '../BasicComponents/VersionLabel';
import { CostCalculator } from '../CostCalculator/index';
import { UserRoleBadges } from './UserRoleBadges';

const { Text } = Typography;

// The label dims itself, and antd dims disabled menu items on top of that, which together made
// it unreadable. Override both and lean on antd's own secondary-text token instead.
const MenuVersion = styled(VersionLabel)`
  opacity: 0.8;
  color: ${staticThemeToken.colorTextSecondary};
`;

export const useUserMenuItems = () => {
  const currentUser = useCurrentUser();
  const isChild = useIsChildAccount();
  const isAdmin = useIsAdmin();
  const [costCalcOpen, setCostCalcOpen] = useState(false);

  if (currentUser == null) {
    return { groupItems: [], dropdownItems: [], costCalcModal: null };
  }

  const userItems: NonNullable<MenuProps['items']> = [
    {
      key: 'account',
      label: <Link to={`/admin/account/${currentUser.id}`}>Account</Link>,
    },
    ...(isChild
      ? []
      : [
          { key: 'cost', label: 'Cost Calculator', onClick: () => setCostCalcOpen(true) },
          {
            key: 'billing',
            label: <Link to={`/admin/account/${currentUser.id}/billing`}>Billing</Link>,
          },
        ]),
    { key: 'updates', label: <UpdateStream>Updates</UpdateStream> },
    {
      key: 'logout',
      label: (
        <Link to="/auth/logout" id="nav_link_logout">
          Logout
        </Link>
      ),
    },
    { type: 'divider' },
    // Not clickable — it is here so support can ask the user to read it out.
    { key: 'version', label: <MenuVersion />, disabled: true },
    ...(isNewBuildAvailable()
      ? [
          {
            key: 'build-update',
            disabled: true,
            // antd menu items are fixed-height single-liners; this label is two lines.
            style: { height: 'auto', lineHeight: 1.4, paddingTop: 6, paddingBottom: 6 },
            label: (
              <Space>
                <IconExclamationTriangle
                  style={{ color: staticThemeToken.colorTextSecondary, opacity: 0.8 }}
                />
                <Text type="secondary">New version available</Text>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => {
                    trackEvent('build_update_reload', { reason: 'user_menu_click' });
                    window.location.reload();
                  }}
                >
                  <Text type="secondary" underline>
                    Reload
                  </Text>
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const groupItems: NonNullable<MenuProps['items']> = [
    {
      key: 'user',
      label: (
        <Flex justify="space-between" align="center" gap={8}>
          <span>User</span>
          <UserRoleBadges />
        </Flex>
      ),
      children: userItems,
    },
  ];

  const costCalcModal = isChild ? null : (
    <Modal
      title="Estimate costs for geolocating"
      open={costCalcOpen}
      onCancel={() => setCostCalcOpen(false)}
      footer={null}
      width={800}
    >
      <CostCalculator user={currentUser} isAdmin={isAdmin} />
    </Modal>
  );

  return { groupItems, dropdownItems: userItems, costCalcModal };
};
